document.documentElement.classList.remove('no-js');

const THEME = window.AmalengoTheme || {};

function formatMoney(cents, format = THEME.moneyFormat || '€{{amount_with_comma_separator}}') {
  const number = Number(cents || 0);
  const formatValue = (precision = 2, thousands = '.', decimal = ',') => {
    const parts = (number / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[0] + (parts[1] ? decimal + parts[1] : '');
  };
  const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
  let value = formatValue(2, ',', '.');
  if (token === 'amount_with_comma_separator') value = formatValue(2, '.', ',');
  if (token === 'amount_no_decimals') value = formatValue(0, ',', '.');
  if (token === 'amount_no_decimals_with_comma_separator') value = formatValue(0, '.', ',');
  return format.replace(/\{\{\s*\w+\s*\}\}/, value);
}

function escapeHtml(value = '') {
  const element = document.createElement('div');
  element.textContent = value;
  return element.innerHTML;
}

class BeforeAfterSlider extends HTMLElement {
  connectedCallback() {
    this.range = this.querySelector('[data-before-after-range]');
    if (!this.range) return;
    this.update = this.update.bind(this);
    this.range.addEventListener('input', this.update);
    this.update();
  }
  disconnectedCallback() { this.range?.removeEventListener('input', this.update); }
  update() {
    const value = Math.max(0, Math.min(100, Number(this.range.value)));
    this.style.setProperty('--ba-position', `${value}%`);
    this.range.setAttribute('aria-valuetext', `${value} Prozent des Nachher-Bildes sichtbar`);
  }
}
if (!customElements.get('before-after-slider')) customElements.define('before-after-slider', BeforeAfterSlider);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = text;
        const recommendations = wrapper.querySelector('product-recommendations');
        if (recommendations?.innerHTML.trim()) {
          this.innerHTML = recommendations.innerHTML;
          this.hidden = false;
        }
      })
      .catch(() => {});
  }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

class AjaxCartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.backdrop = document.querySelector('[data-cart-backdrop]');
    if (!this.drawer) return;
    this.items = this.drawer.querySelector('[data-cart-items]');
    this.total = this.drawer.querySelector('[data-cart-total]');
    this.progress = this.drawer.querySelector('[data-cart-progress]');
    this.progressText = this.drawer.querySelector('[data-cart-progress-text]');
    this.threshold = Number(this.drawer.dataset.shippingThreshold || 0);
    this.bind();
    this.startTimer();
  }

  bind() {
    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-cart-open]');
      if (open) { event.preventDefault(); this.open(); }
      if (event.target.closest('[data-cart-close]') || event.target === this.backdrop) this.close();

      const drawerQty = event.target.closest('[data-drawer-qty]');
      if (drawerQty) this.change(Number(drawerQty.dataset.line), Number(drawerQty.dataset.quantity));

      const remove = event.target.closest('[data-drawer-remove]');
      if (remove) this.change(Number(remove.dataset.line), 0);

      const upsell = event.target.closest('[data-upsell-add]');
      if (upsell) this.addVariant(Number(upsell.dataset.variantId), upsell);

      const qtyButton = event.target.closest('[data-qty-button]');
      if (qtyButton) {
        const input = qtyButton.parentElement.querySelector('input[type="number"]');
        const step = qtyButton.dataset.qtyButton === 'plus' ? 1 : -1;
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
      }

      const thumb = event.target.closest('[data-media-thumb]');
      if (thumb) {
        const gallery = thumb.closest('[data-product-gallery]');
        gallery.querySelectorAll('[data-product-media]').forEach((item) => item.hidden = true);
        gallery.querySelector(`[data-media-id="${thumb.dataset.mediaThumb}"]`)?.removeAttribute('hidden');
        gallery.querySelectorAll('[data-media-thumb]').forEach((item) => item.removeAttribute('aria-current'));
        thumb.setAttribute('aria-current', 'true');
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-ajax-product-form]');
      if (!form) return;
      event.preventDefault();
      this.addForm(form);
    });

    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
  }

  async json(url, options = {}) {
    const response = await fetch(url, { headers: { Accept: 'application/json', ...(options.headers || {}) }, ...options });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || 'Die Warenkorb-Aktion ist fehlgeschlagen.');
    return data;
  }

  async addForm(form) {
    const button = form.querySelector('[type="submit"]');
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = 'Wird hinzugefügt …'; }
    try {
      const response = await fetch(`${THEME.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || 'Produkt konnte nicht hinzugefügt werden.');
      }
      await this.refresh();
      this.open(false);
    } catch (error) {
      alert(error.message);
    } finally {
      if (button) { button.disabled = false; button.textContent = original; }
    }
  }

  async addVariant(id, button) {
    const original = button.textContent;
    button.disabled = true; button.textContent = '…';
    try {
      await this.json(`${THEME.cartAdd}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] })
      });
      await this.refresh();
    } catch (error) { alert(error.message); }
    finally { button.disabled = false; button.textContent = original; }
  }

  async change(line, quantity) {
    try {
      const cart = await this.json(`${THEME.cartChange}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      this.render(cart);
    } catch (error) { alert(error.message); }
  }

  async refresh() {
    const cart = await this.json(`${THEME.root}cart.js`);
    this.render(cart);
  }

  render(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((target) => {
      target.textContent = cart.item_count;
      target.hidden = cart.item_count === 0;
    });

    if (!cart.items.length) {
      this.items.innerHTML = '<div class="drawer-empty"><h3>Dein Warenkorb ist leer</h3><p>Dein neues Lieblingsstück wartet bereits.</p></div>';
    } else {
      this.items.innerHTML = cart.items.map((item, index) => `
        <article class="drawer-item">
          <a href="${item.url}">${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="160" height="160">` : ''}</a>
          <div>
            <a class="drawer-item__title" href="${item.url}">${escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="drawer-item__variant">${escapeHtml(item.variant_title)}</div>` : ''}
            <div class="drawer-item__actions">
              <div class="drawer-qty">
                <button type="button" data-drawer-qty data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-drawer-qty data-line="${index + 1}" data-quantity="${item.quantity + 1}">+</button>
              </div>
              <button class="drawer-remove" type="button" data-drawer-remove data-line="${index + 1}">Entfernen</button>
            </div>
          </div>
          <strong>${formatMoney(item.final_line_price)}</strong>
        </article>`).join('');
    }

    this.total.textContent = formatMoney(cart.total_price);
    this.updateProgress(cart.total_price);
  }

  updateProgress(total) {
    if (!this.threshold) return;
    const percent = Math.min(100, (total / this.threshold) * 100);
    this.progress.style.width = `${percent}%`;
    const remaining = Math.max(0, this.threshold - total);
    this.progressText.textContent = remaining > 0
      ? `Noch ${formatMoney(remaining)} bis zum kostenlosen Versand`
      : 'Kostenloser Versand ist freigeschaltet ✓';
  }

  startTimer() {
    const timer = this.drawer.querySelector('[data-cart-timer]');
    if (!timer) return;
    const initial = Number(timer.dataset.minutes || 10) * 60;
    let remaining = initial;
    const tick = () => {
      timer.textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
      remaining = remaining > 0 ? remaining - 1 : initial;
    };
    tick(); setInterval(tick, 1000);
  }

  open(refresh = true) {
    document.body.classList.add('drawer-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (refresh) this.refresh().catch(() => {});
    this.drawer.querySelector('[data-cart-close]')?.focus();
  }

  close() {
    document.body.classList.remove('drawer-open');
    this.drawer.setAttribute('aria-hidden', 'true');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ajaxCartDrawer = new AjaxCartDrawer();
  document.querySelectorAll('[data-product-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const root = select.closest('[data-product-root]');
      const price = root?.querySelector('[data-product-price]');
      if (price && option.dataset.price) price.textContent = option.dataset.price;
    });
  });
});
